// frontend/src/components/CartoesAnalytics/UsoPorDiaChart.jsx
//
// "Em que dias eu mais uso o cartão." O gráfico só vira decisão quando cruzado com
// o dia de fechamento: comprar logo depois do fechamento rende quase um mês a mais
// de prazo; comprar na véspera rende poucos dias. Por isso as linhas de referência
// e o prazo médio ao lado.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Card, Row, Segmented, Select, Space, Typography, DatePicker,
    Tooltip, message, Empty, Spin, Alert, Table,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
    ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip as RTooltip, Legend, ReferenceLine,
} from 'recharts';
import { getCartaoUsoPorDia, getCartoes } from '../../services/cardService';
import { chartPalette, brandColors } from '../../styles/brandColors';
import { getErrorMessage } from '../../utils/errorHandler';
import KpiCard from './KpiCard';
import './CartoesAnalytics.css';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const fmtBRL = (v) =>
    parseFloat(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const fmtCompacto = (v) => {
    const n = Number(v) || 0;
    if (Math.abs(n) >= 1000) {
        const milhares = (n / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 });
        return `R$ ${milhares}k`;
    }
    return `R$ ${Math.round(n)}`;
};

const PRESETS = [
    { label: 'Últimos 3 meses', value: [dayjs().subtract(2, 'month').startOf('month'), dayjs()] },
    { label: 'Últimos 6 meses', value: [dayjs().subtract(5, 'month').startOf('month'), dayjs()] },
    { label: 'Últimos 12 meses', value: [dayjs().subtract(11, 'month').startOf('month'), dayjs()] },
    { label: 'Este ano', value: [dayjs().startOf('year'), dayjs()] },
];

const RANGE_PADRAO = [dayjs().subtract(5, 'month').startOf('month'), dayjs()];

// Cor por entidade (id do cartão), nunca por posição no ranking.
const corDoCartao = (cartaoId, ordemIds) => {
    const idx = ordemIds.indexOf(cartaoId);
    return chartPalette[(idx < 0 ? 0 : idx) % chartPalette.length];
};

const TooltipUso = ({ active, payload, label, metrica, eixo, nomePorId }) => {
    if (!active || !payload?.length) return null;
    const itens = payload.filter((p) => p.value > 0).sort((a, b) => b.value - a.value);
    if (!itens.length) return null;
    const total = itens.reduce((acc, p) => acc + p.value, 0);
    const formatar = (v) => (metrica === 'valor' ? fmtBRL(v) : `${v} ${v === 1 ? 'compra' : 'compras'}`);
    return (
        <div className="cartoes-analytics-tooltip">
            <div className="cartoes-analytics-tooltip-titulo">
                {eixo === 'dia' ? `Dia ${label}` : label}
            </div>
            {itens.map((p) => (
                <div key={p.dataKey} className="cartoes-analytics-tooltip-linha">
                    <span className="cartoes-analytics-tooltip-bullet" style={{ background: p.color }} />
                    <span className="cartoes-analytics-tooltip-rotulo">
                        {nomePorId[p.dataKey.replace('c_', '')] || 'Total'}
                    </span>
                    <span>{formatar(p.value)}</span>
                </div>
            ))}
            {itens.length > 1 && (
                <div className="cartoes-analytics-tooltip-total">
                    <span>Total</span>
                    <span>{formatar(total)}</span>
                </div>
            )}
        </div>
    );
};

const UsoPorDiaChart = ({ cartaoIdFixo = null, isMobile = false }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [periodo, setPeriodo] = useState(RANGE_PADRAO);
    const [cartaoSel, setCartaoSel] = useState('todos');
    const [metrica, setMetrica] = useState('valor');
    const [eixo, setEixo] = useState('dia');
    const [todosCartoes, setTodosCartoes] = useState([]);
    const [messageApi, contextHolder] = message.useMessage();

    const cartaoFiltro = cartaoIdFixo || (cartaoSel === 'todos' ? null : cartaoSel);

    useEffect(() => {
        if (cartaoIdFixo) return;
        let ativo = true;
        getCartoes()
            .then((lista) => { if (ativo) setTodosCartoes(lista || []); })
            .catch(() => {});
        return () => { ativo = false; };
    }, [cartaoIdFixo]);

    const fetchDados = useCallback(async () => {
        if (!periodo?.[0] || !periodo?.[1]) return;
        setLoading(true);
        try {
            const resposta = await getCartaoUsoPorDia({
                startDate: periodo[0].format('YYYY-MM-DD'),
                endDate: periodo[1].format('YYYY-MM-DD'),
                cartoes: cartaoFiltro ? [cartaoFiltro] : undefined,
            });
            setData(resposta);
        } catch (err) {
            messageApi.error(getErrorMessage(err, 'Não foi possível carregar o uso dos cartões.'));
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [periodo, cartaoFiltro, messageApi]);

    useEffect(() => {
        fetchDados();
    }, [fetchDados]);

    const cartoes = useMemo(() => data?.cartoes || [], [data]);
    const ordemIds = useMemo(
        () => (todosCartoes.length ? todosCartoes : cartoes).map((c) => c.id),
        [todosCartoes, cartoes],
    );
    const nomePorId = useMemo(
        () => Object.fromEntries(cartoes.map((c) => [String(c.id), c.nome])),
        [cartoes],
    );

    // Uma série por cartão só faz sentido empilhada quando há mais de um cartão no
    // recorte; com um só, série única e sem legenda (o título já a nomeia).
    const empilhado = cartoes.length > 1;

    const dadosGrafico = useMemo(() => {
        if (!data) return [];
        if (eixo === 'semana') {
            return (data.por_dia_semana || []).map((d) => ({
                chave: d.label,
                total: metrica === 'valor' ? d.valor : d.quantidade,
            }));
        }
        return (data.por_dia || []).map((d) => {
            const linha = {
                chave: d.dia,
                total: metrica === 'valor' ? d.valor : d.quantidade,
            };
            if (empilhado) {
                cartoes.forEach((c) => {
                    const v = d.por_cartao?.[String(c.id)];
                    linha[`c_${c.id}`] = (metrica === 'valor' ? v?.valor : v?.quantidade) || 0;
                });
            }
            return linha;
        });
    }, [data, eixo, metrica, empilhado, cartoes]);

    // Linhas de referência só com um cartão em vista: cada cartão fecha num dia
    // diferente, e marcar vários fechamentos no mesmo eixo não diz nada.
    const cartaoUnico = cartoes.length === 1 ? cartoes[0] : null;
    const mostraReferencias = eixo === 'dia' && !!cartaoUnico;

    const destaque = useMemo(() => {
        if (!cartaoUnico || !data?.janela_pre_fechamento_dias) return null;
        if ((cartaoUnico.pct_pre_fechamento || 0) < 30) return null;
        return {
            pct: cartaoUnico.pct_pre_fechamento,
            dias: data.janela_pre_fechamento_dias,
            nome: cartaoUnico.nome,
            fechamento: cartaoUnico.dia_fechamento,
        };
    }, [cartaoUnico, data]);

    const temDados = (data?.totais?.quantidade || 0) > 0;

    // Pico do recorte inteiro (o que o gráfico desenha), não o do cartão campeão.
    const diaPicoGeral = useMemo(() => {
        const dias = data?.por_dia || [];
        const campo = metrica === 'valor' ? 'valor' : 'quantidade';
        let melhor = null;
        dias.forEach((d) => {
            if (d[campo] > 0 && (!melhor || d[campo] > melhor[campo])) melhor = d;
        });
        return melhor?.dia || null;
    }, [data, metrica]);

    const colunasCartoes = [
        { title: 'Cartão', dataIndex: 'nome', key: 'nome', ellipsis: true,
          render: (nome, r) => (
              <Space size={6}>
                  <span className="cartoes-analytics-bullet" style={{ background: corDoCartao(r.id, ordemIds) }} />
                  {nome}
              </Space>
          ) },
        { title: 'Gasto', dataIndex: 'total_valor', key: 'total_valor', align: 'right',
          render: (v) => fmtBRL(v) },
        { title: 'Compras', dataIndex: 'total_quantidade', key: 'total_quantidade', align: 'right' },
        { title: 'Ticket médio', dataIndex: 'ticket_medio', key: 'ticket_medio', align: 'right',
          responsive: ['md'], render: (v) => fmtBRL(v) },
        { title: 'Dia mais usado', dataIndex: 'dia_pico', key: 'dia_pico', align: 'right',
          render: (v) => (v ? `dia ${v}` : '—') },
        { title: 'Fecha dia', dataIndex: 'dia_fechamento', key: 'dia_fechamento', align: 'right',
          responsive: ['md'] },
        {
            title: (
                <Space size={4}>
                    Prazo médio
                    <Tooltip title="Média de dias entre a compra e o vencimento da fatura em que ela caiu. Quanto maior, mais tempo o dinheiro ficou com você.">
                        <InfoCircleOutlined style={{ color: brandColors.accent }} />
                    </Tooltip>
                </Space>
            ),
            dataIndex: 'prazo_medio_dias', key: 'prazo_medio_dias', align: 'right',
            render: (v) => (v == null ? '—' : `${v} dias`),
        },
    ];

    const controles = (
        <Space wrap size={8}>
            <RangePicker
                value={periodo}
                onChange={(v) => v && setPeriodo(v)}
                format="DD/MM/YYYY"
                allowClear={false}
                presets={PRESETS}
                style={{ width: isMobile ? '100%' : 240 }}
            />
            {!cartaoIdFixo && (
                <Select
                    value={cartaoSel}
                    onChange={setCartaoSel}
                    style={{ minWidth: 160 }}
                    options={[
                        { value: 'todos', label: 'Todos os cartões' },
                        ...todosCartoes.map((c) => ({ value: c.id, label: c.nome })),
                    ]}
                />
            )}
            <Segmented
                value={metrica}
                onChange={setMetrica}
                options={[
                    { label: 'Valor', value: 'valor' },
                    { label: 'Nº de compras', value: 'quantidade' },
                ]}
            />
            <Segmented
                value={eixo}
                onChange={setEixo}
                options={[
                    { label: 'Dia do mês', value: 'dia' },
                    { label: 'Dia da semana', value: 'semana' },
                ]}
            />
        </Space>
    );

    const formatarEixoY = metrica === 'valor' ? fmtCompacto : (v) => v;

    return (
        <div className="cartoes-analytics">
            {contextHolder}

            <Row gutter={[12, 12]} className="cartoes-analytics-kpi-row">
                <KpiCard
                    titulo="Gasto no período"
                    valor={data?.totais?.valor || 0}
                    moeda
                    loading={loading}
                    isMobile={isMobile}
                />
                <KpiCard
                    titulo="Compras"
                    valor={data?.totais?.quantidade || 0}
                    loading={loading}
                    isMobile={isMobile}
                />
                <KpiCard
                    titulo="Ticket médio"
                    valor={data?.totais?.ticket_medio || 0}
                    moeda
                    loading={loading}
                    isMobile={isMobile}
                />
                <KpiCard
                    titulo="Dia mais usado"
                    valor={diaPicoGeral ? `dia ${diaPicoGeral}` : '—'}
                    legenda={diaPicoGeral ? (metrica === 'valor' ? 'por valor' : 'por nº') : null}
                    loading={loading}
                    isMobile={isMobile}
                />
            </Row>

            {destaque && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={`${destaque.pct}% do gasto no ${destaque.nome} sai nos ${destaque.dias} dias antes do fechamento`}
                    description={`O cartão fecha no dia ${destaque.fechamento}. Compras feitas logo depois do fechamento entram na fatura seguinte e ganham quase um mês a mais de prazo.`}
                />
            )}

            <Card
                size="small"
                className="cartoes-analytics-chart-card"
                title={eixo === 'dia' ? 'Uso por dia do mês' : 'Uso por dia da semana'}
                extra={!isMobile ? controles : null}
                style={{ marginBottom: 16 }}
            >
                {isMobile && <div style={{ marginBottom: 12 }}>{controles}</div>}

                <Spin spinning={loading}>
                    {!temDados && !loading ? (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description="Nenhuma compra de cartão no período selecionado."
                        />
                    ) : (
                        <ResponsiveContainer width="100%" height={isMobile ? 260 : 320}>
                            <BarChart data={dadosGrafico} margin={{ top: 28, right: 8, left: 0, bottom: 8 }}>
                                <CartesianGrid stroke="#f0f0f0" vertical={false} />
                                <XAxis
                                    dataKey="chave"
                                    tick={{ fontSize: 11 }}
                                    interval={eixo === 'dia' && isMobile ? 2 : 0}
                                />
                                <YAxis tickFormatter={formatarEixoY} tick={{ fontSize: 11 }} width={56} />
                                <RTooltip
                                    content={<TooltipUso metrica={metrica} eixo={eixo} nomePorId={nomePorId} />}
                                    cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                                />
                                {empilhado && eixo === 'dia' && <Legend wrapperStyle={{ fontSize: 12, paddingTop: 12 }} />}

                                {mostraReferencias && (
                                    <ReferenceLine
                                        x={cartaoUnico.dia_fechamento}
                                        stroke={brandColors.danger}
                                        strokeWidth={2}
                                        label={{ value: 'fecha', position: 'top', fontSize: 11, fill: brandColors.danger }}
                                    />
                                )}
                                {mostraReferencias && cartaoUnico.dia_vencimento !== cartaoUnico.dia_fechamento && (
                                    <ReferenceLine
                                        x={cartaoUnico.dia_vencimento}
                                        stroke={brandColors.textSecondary}
                                        strokeDasharray="4 4"
                                        strokeWidth={2}
                                        label={{ value: 'vence', position: 'top', fontSize: 11, fill: brandColors.textSecondary }}
                                    />
                                )}

                                {empilhado && eixo === 'dia' ? (
                                    cartoes.map((c, idx) => (
                                        <Bar
                                            key={c.id}
                                            dataKey={`c_${c.id}`}
                                            name={c.nome}
                                            stackId="uso"
                                            fill={corDoCartao(c.id, ordemIds)}
                                            stroke={brandColors.surface}
                                            strokeWidth={1}
                                            radius={idx === cartoes.length - 1 ? [4, 4, 0, 0] : 0}
                                            maxBarSize={28}
                                        />
                                    ))
                                ) : (
                                    <Bar
                                        dataKey="total"
                                        name={cartaoUnico ? cartaoUnico.nome : 'Todos os cartões'}
                                        fill={cartaoUnico
                                            ? corDoCartao(cartaoUnico.id, ordemIds)
                                            : brandColors.textPrimary}
                                        radius={[4, 4, 0, 0]}
                                        maxBarSize={eixo === 'semana' ? 56 : 28}
                                    />
                                )}
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </Spin>

                {mostraReferencias && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Barras à direita da linha “fecha” caem na fatura seguinte — mais prazo para pagar.
                    </Text>
                )}
            </Card>

            {temDados && cartoes.length > 0 && (
                <Card size="small" title="Resumo por cartão" style={{ marginBottom: 16 }}>
                    <Table
                        rowKey="id"
                        size="small"
                        columns={colunasCartoes}
                        dataSource={cartoes}
                        pagination={false}
                        scroll={{ x: 'max-content' }}
                    />
                </Card>
            )}

            <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                Conta eventos de compra, não parcelas: uma compra em 12x aparece uma vez, no dia em
                que foi feita, pelo valor total. Encargos de fatura (juros, IOF, anuidade) ficam fora.
            </Text>
        </div>
    );
};

export default UsoPorDiaChart;
